import { createSelector } from "reselect";
import { isArray } from "lodash";
import { GlobalEntityType } from "shared/types";
import { ErrorType, ERROR_CODE_BAD_REQUEST, ERROR_CODE_CONFLICT, INTERNAL_SERVER_ERROR_CODE, NO_ACCESS_MESSAGE, } from "api/api-error";
import { isNotEmpty } from "utils/validate";
import { isInUseSelector as isCellModelInUseSelector } from "../cell-model/cell-model.selectors";
import { isInUseSelector as isRecipeInUseSelector } from "../recipe/recipe.selectors";
import { cellModelsSelector } from "store/cell-model-list/cell-model-list.selectors";
import { recipesSelector } from "store/recipe-list/recipe-list.selectors";
import { workspacesSelector } from "store/rs-workspace-list/rs-workspace-list.selectors";
import { userInfoSelector } from "store/auth/auth.selectors";
import { detailsKnowledgeItemToViewSelector, rsTagsListSelector } from "store/global/global.selectors";
import { Roles } from "store/auth/auth.types";
import { getTagAsObject, isSampleObject } from "./knowledge-base-item.utils";
import { processDataListSelector } from "store/process-data-list/process-data-list.selectors";
import { EXAMPLE_TAG } from "shared/rsDictionary";
import { getWorkspaceStateSelector } from "store/ps-workspace/ps-workspace.selectors";
import { checkIsUserOwner } from "store/auth/auth.utils";
export const getKnowledgeBaseItemState = (state) => state.knowledgeBaseItem;
export const isInProgressSelector = createSelector(getKnowledgeBaseItemState, (state) => state.inProgress);
export const entityTypeSelector = createSelector(getKnowledgeBaseItemState, (state) => state.entityType);
export const isSubscribedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.isSubscribed);
export const isSocketStartedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.isSocketStarted);
export const isInitedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.inited);
export const currentItemIdentifierSelector = createSelector(getKnowledgeBaseItemState, (state) => ({
    entityType: state.entityType,
    saved: state.saved,
    id: state.id,
}));
export const isSavedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.saved);
export const tagsSelector = createSelector(getKnowledgeBaseItemState, (state) => state.tags);
export const tagsSavingSelector = createSelector(getKnowledgeBaseItemState, (state) => state.tagsSaving);
const serverErrorSelector = createSelector(getKnowledgeBaseItemState, (state) => state.serverError);
export const serverErrorsSelector = (requestTemplate, additionalStatusCodes, entityId) => createSelector(getKnowledgeBaseItemState, (state) => {
    const requestTemplates = isNotEmpty(requestTemplate)
        ? Array.isArray(requestTemplate)
            ? requestTemplate
            : [requestTemplate]
        : [];
    const { serverError, currentRequestTemplate } = state;
    if (serverError === null ||
        (requestTemplates.length > 0 && !requestTemplates.includes(currentRequestTemplate ?? undefined))) {
        return null;
    }
    if (isNotEmpty(entityId) && serverError.entityId !== entityId) {
        return null;
    }
    const { status, data: { errors = [] } = {} } = serverError.error;
    if (status &&
        [ERROR_CODE_BAD_REQUEST, ERROR_CODE_CONFLICT, ...(additionalStatusCodes ?? [])].includes(status)) {
        return !errors.length ? null : errors;
    }
    else {
        return null;
    }
});
export const socketErrorSelector = createSelector(getKnowledgeBaseItemState, (state) => state.socketError);
export const isInternalServerErrorSelector = createSelector(serverErrorSelector, (serverError) => serverError?.error?.status === INTERNAL_SERVER_ERROR_CODE);
export const restrictionsSelector = (payload) => createSelector(getKnowledgeBaseItemState, (state) => {
    if (!payload || Object.keys(payload).every((i) => i === undefined)) {
        return state.restrictions;
    }
    const { rsEndpoint, template, method } = payload;
    let restrictions;
    if (rsEndpoint !== undefined && state.restrictions[rsEndpoint]) {
        const endpointRestrictions = state.restrictions[rsEndpoint];
        restrictions = endpointRestrictions;
        if (template !== undefined && endpointRestrictions) {
            const templateRestrictions = endpointRestrictions[template];
            restrictions = templateRestrictions;
            if (method !== undefined && templateRestrictions) {
                restrictions = templateRestrictions[method];
            }
        }
    }
    return restrictions;
});
export const isEditableSelector = createSelector(getKnowledgeBaseItemState, (state) => state.editable);
export const isInUseSelector = (entityType) => createSelector(isCellModelInUseSelector, isRecipeInUseSelector, (isCellModelInUse, isRecipeInUse) => {
    switch (entityType) {
        case GlobalEntityType.CellModel:
            return isCellModelInUse;
        case GlobalEntityType.Recipe:
            return isRecipeInUse;
    }
    return false;
});
export const knowledgeBaseItemNameSelector = createSelector(getKnowledgeBaseItemState, (state) => state.name);
export const duplicatingItemSelector = createSelector(getKnowledgeBaseItemState, cellModelsSelector, recipesSelector, processDataListSelector, workspacesSelector, (knowledgeBaseItem, cellModels, recipes, processDatas, workspaces) => {
    if (knowledgeBaseItem.duplicating === null) {
        return { duplicatingItem: null };
    }
    const { duplicating, entityType, description, tags, id, createdBy, name } = knowledgeBaseItem;
    const { id: duplicatingId, workspaceId, entityType: duplicatingEntityType } = duplicating;
    if (entityType === duplicatingEntityType) {
        const isSample = isSampleObject(createdBy ?? null);
        return {
            duplicatingItem: duplicatingId === null
                ? null
                : {
                    name,
                    description,
                    tags: isSample ? (tags ?? []).filter(({ name }) => name !== EXAMPLE_TAG) : tags,
                    id,
                    entityType,
                },
            workspaceId,
        };
    }
    if (duplicatingId !== null) {
        let duplicatingItem;
        switch (duplicatingEntityType) {
            case GlobalEntityType.CellModel:
                duplicatingItem = cellModels.find(({ id }) => id === duplicatingId);
                break;
            case GlobalEntityType.Recipe:
                duplicatingItem = recipes.find(({ id }) => id === duplicatingId);
                break;
            case GlobalEntityType.ProcessData:
                duplicatingItem = processDatas.find(({ id }) => id === duplicatingId);
                break;
            case GlobalEntityType.RsWorkspace:
                duplicatingItem = workspaces.find(({ id }) => id === duplicatingId);
                break;
        }
        const { name, tags, description, createdBy } = duplicatingItem || {};
        const isSample = isSampleObject(createdBy ?? null);
        return {
            duplicatingItem: {
                name,
                tags: isSample ? (tags ?? []).filter(({ name }) => name !== EXAMPLE_TAG) : tags,
                description: isSample ? "" : (description ?? ""),
                id: duplicatingId,
                entityType: duplicatingEntityType,
            },
            workspaceId,
        };
    }
    return { duplicatingItem: null, workspaceId };
});
export const upsertInitialDataSelector = createSelector(duplicatingItemSelector, rsTagsListSelector, ({ duplicatingItem }, tags) => ({
    name: duplicatingItem ? `Copy of ${duplicatingItem.name}` : "",
    description: duplicatingItem?.description ?? "",
    tags: duplicatingItem?.tags?.map((tag) => getTagAsObject(tag, tags)),
}));
export const deleteTargetSelector = createSelector(getWorkspaceStateSelector, getKnowledgeBaseItemState, (psState, kbiState) => psState.deleteTarget ?? kbiState.deleteTarget);
export const preventNavigationSelector = createSelector(getKnowledgeBaseItemState, (state) => state.preventNavigation);
export const dependentWorkspacesSelector = createSelector(getKnowledgeBaseItemState, (state) => {
    if (isArray(state.dependentWorkspaces)) {
        return state.dependentWorkspaces;
    }
    return [];
});
export const shouldDependentWorkspacesDialogOpenSelector = createSelector(dependentWorkspacesSelector, (dependentWorkspaces) => dependentWorkspaces.length > 0);
export const dependentCallbackSelector = createSelector(getKnowledgeBaseItemState, (state) => state.dependentCallback);
export const isDependentDialogOpenedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.isDependentDialogOpened);
export const isShareOwnershipDialogOpenedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.isShareOwnershipDialogOpened);
export const isRevokeOwnershipDialogOpenedSelector = createSelector(getKnowledgeBaseItemState, (state) => state.isRevokeOwnershipDialogOpened);
export const isOwnershipEditInProgressSelector = createSelector(getKnowledgeBaseItemState, (state) => state.ownershipEditInProgress);
export const canUserEditKnowledgeBaseItemDetailsSelector = (isProcessDataItem = false) => createSelector(userInfoSelector, detailsKnowledgeItemToViewSelector, (userInfo, detailsItem) => {
    if (!detailsItem || !userInfo) {
        return false;
    }
    const { owners = [], createdBy } = detailsItem;
    const isOwner = owners.some(({ id }) => id === userInfo.id);
    const isSample = isSampleObject(createdBy);
    return (!isSample &&
        (isProcessDataItem ||
            isOwner ||
            [Roles.SuperAdmin, Roles.SuperUser].includes(userInfo.role ?? Roles.User)));
});
export const noAccessSelector = createSelector(getKnowledgeBaseItemState, socketErrorSelector, userInfoSelector, ({ isSubscribed, owners }, socketError, userInfo) => {
    const noAccess = !isSubscribed &&
        !!socketError?.errors.some(({ errorType, text }) => errorType === ErrorType.Validation && text === NO_ACCESS_MESSAGE);
    const userHasRightsToEditEntity = checkIsUserOwner(userInfo, owners) ||
        (!!userInfo?.role && [Roles.SuperAdmin, Roles.SuperUser].includes(userInfo?.role));
    return noAccess || !userHasRightsToEditEntity;
});
